import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ticketsAPI, dashboardAPI } from "../services/api";
import {
  MessageSquare,
  Clock,
  CheckCircle,
  TrendingUp,
  Users,
  Target,
  BarChart3,
  AlertCircle,
  Tag,
  User,
  XCircle,
  RefreshCw,
  LayoutDashboard,
  Star,
  PieChart as PieChartIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { formatDuration } from "@/utils/formatters";

const Dashboard = () => {
  const { user } = useAuth();
  const canMonitorSupport = ["analyst", "admin"].includes(user?.role);

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [analytics, setAnalytics] = useState({
    peak_hours: [],
    top_attendants: [],
    volume: [],
    top_clients: [],
    stats: {
      total_tickets: 0,
      resolved_tickets: 0,
      resolution_rate: 0,
      avg_duration_seconds: 0,
    },
  });
  const [dateFilter, setDateFilter] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  const PRIORITY_COLORS = {
    Baixa: "#10b981",
    Média: "#f59e0b",
    Alta: "#f97316",
    Urgente: "#ef4444",
  };

  useEffect(() => {
    if (!user) return;

    loadData();
    if (canMonitorSupport) {
      loadAnalytics();
    }

    const intervalId = setInterval(() => {
      loadData();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [user, canMonitorSupport]);

  const loadData = async () => {
    try {
      const response = await ticketsAPI.getAll({ limit: 1000 });
      const todosTickets = response.data.items || response.data;

      const hoje = new Date().toLocaleDateString("pt-BR");

      const ticketsEmAndamento = todosTickets.filter((ticket) => {
        const temPermissao = canMonitorSupport || ticket.assigned_to === user.id;
        return ticket.status === "in_progress" && temPermissao;
      });

      setRecentTickets(ticketsEmAndamento);

      const estatisticasDoUsuario = todosTickets.filter(
        (ticket) => canMonitorSupport || ticket.assigned_to === user.id,
      );

      const pendingCount = estatisticasDoUsuario.filter(
        (t) => t.status === "pending",
      ).length;
      const inProgressCount = estatisticasDoUsuario.filter(
        (t) => t.status === "in_progress",
      ).length;
      const resolvedCount = estatisticasDoUsuario.filter((t) => {
        if (t.status !== "resolved" || !t.updated_at) return false;
        return new Date(t.updated_at).toLocaleDateString("pt-BR") === hoje;
      }).length;
      const closedCount = estatisticasDoUsuario.filter((t) => {
        if (t.status !== "closed" || !t.updated_at) return false;
        return new Date(t.updated_at).toLocaleDateString("pt-BR") === hoje;
      }).length;

      const newStats = {
        total: pendingCount + inProgressCount + resolvedCount + closedCount,
        pending: pendingCount,
        in_progress: inProgressCount,
        resolved: resolvedCount,
        closed: closedCount,
      };

      setStats(newStats);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoadingAnalytics(true);
      const response = await dashboardAPI.getAnalytics(
        dateFilter.start,
        dateFilter.end,
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleDateFilterChange = () => {
    if (canMonitorSupport) loadAnalytics();
  };

  const COLORS = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800",
    in_progress: "bg-blue-100 text-blue-800",
    resolved: "bg-green-100 text-green-800",
    closed: "bg-gray-100 text-gray-800",
  };

  const statusLabels = {
    pending: "Pendente",
    in_progress: "Em Andamento",
    resolved: "Resolvido",
    closed: "Fechado",
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <LayoutDashboard className="w-8 h-8 text-blue-600 shrink-0" />
            Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            {canMonitorSupport
              ? "Visão completa da utilização da plataforma"
              : "Visão geral dos atendimentos em aberto"}
          </p>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 shrink-0" />
          Resumo dos Atendimentos de Hoje
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-3xl font-bold" data-testid="stat-total">
                    {stats.total}
                  </p>
                </div>
                <MessageSquare className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pendentes</p>
                  <p
                    className="text-3xl font-bold text-yellow-600"
                    data-testid="stat-pending"
                  >
                    {stats.pending}
                  </p>
                </div>
                <Clock className="w-10 h-10 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Em Andamento</p>
                  <p
                    className="text-3xl font-bold text-blue-600"
                    data-testid="stat-progress"
                  >
                    {stats.in_progress}
                  </p>
                </div>
                <RefreshCw className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Resolvidos</p>
                  <p
                    className="text-3xl font-bold text-green-600"
                    data-testid="stat-resolved"
                  >
                    {stats.resolved}
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Fechados</p>
                  <p
                    className="text-3xl font-bold text-gray-600"
                    data-testid="stat-closed"
                  >
                    {stats.closed}
                  </p>
                </div>
                <XCircle className="w-10 h-10 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="bg-gray-50/50 border-b pb-4">
          <CardTitle className="text-lg text-gray-800">
            {canMonitorSupport
              ? "Visão Geral: Todos os Atendimentos em Aberto"
              : "Meus Atendimentos em Aberto"}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-4 flex flex-col gap-1 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
            {recentTickets.map((ticket) => (
              <Link
                key={ticket.id}
                to={`/chat/${ticket.id}`}
                className="block p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md hover:border-blue-300 transition-all duration-200 group"
                data-testid={`ticket-item-${ticket.protocol}`}
              >
                <div className="flex flex-col gap-2.5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 w-full min-w-0">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <p 
                        className="font-bold text-gray-800 text-base group-hover:text-blue-600 transition-colors line-clamp-2 break-words leading-snug flex-1 min-w-0"
                        title={ticket.client_name}
                      >
                        {ticket.client_name}
                      </p>

                      <div className="flex flex-wrap items-center gap-1.5 shrink-0 mt-0.5">
                        {ticket.unread_count > 0 && (
                          <span className="flex items-center justify-center bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse shadow-sm w-max">
                            {ticket.unread_count} nova(s)
                          </span>
                        )}

                        {ticket.from_whatsapp && (
                          <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-semibold w-max">
                            WhatsApp
                          </span>
                        )}
                      </div>
                    </div>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold shrink-0 w-max sm:mt-0 mt-1 ${
                        statusColors[ticket.status] || statusColors["pending"]
                      }`}
                    >
                      {statusLabels[ticket.status] || "Desconhecido"}
                    </span>
                  </div>

                  {ticket.initial_message && (
                    <div className="text-sm text-gray-700 bg-blue-50/50 p-2.5 rounded-lg border border-blue-100/50 line-clamp-2">
                      <span className="font-semibold text-blue-800 mr-1">
                        Motivo:
                      </span>
                      {ticket.initial_message}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 text-xs mt-1">
                    <span className="text-gray-500 flex items-center gap-1 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(ticket.created_at).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>

                    <span className="bg-gray-50 text-gray-600 px-2 py-1 rounded-md font-mono border border-gray-100">
                      {ticket.protocol}
                    </span>

                    {ticket.priority && (
                      <span
                        className={`px-2 py-1 rounded-md flex items-center gap-1 font-medium border
                        ${
                          ticket.priority === "urgent"
                            ? "bg-red-50 text-red-700 border-red-200"
                            : ticket.priority === "high"
                              ? "bg-orange-50 text-orange-700 border-orange-200"
                              : ticket.priority === "medium"
                                ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                : "bg-green-50 text-green-700 border-green-200"
                        }`}
                      >
                        <AlertCircle className="w-3.5 h-3.5" />
                        {ticket.priority === "urgent"
                          ? "Urgente"
                          : ticket.priority === "high"
                            ? "Alta"
                            : ticket.priority === "medium"
                              ? "Média"
                              : "Baixa"}
                      </span>
                    )}

                    {ticket.category && ticket.category !== "Sem Categoria" && (
                      <span className="bg-purple-50 text-purple-700 border border-purple-200 px-2 py-1 rounded-md flex items-center gap-1 font-medium">
                        <Tag className="w-3.5 h-3.5" />
                        {ticket.category}
                      </span>
                    )}

                    {ticket.assigned_to_name && (
                      <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-md flex items-center gap-1 font-medium ml-auto">
                        <User className="w-3.5 h-3.5" />
                        {ticket.assigned_to_name}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}

            {recentTickets.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">
                  Nenhum atendimento registrado hoje.
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Seus tickets em aberto aparecerão aqui automaticamente.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {canMonitorSupport && !loadingAnalytics && (
        <>
          <div>
            <h2 className="text-xl font-semibold mb-4 mt-8 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 shrink-0" />
              Estatísticas Gerais do Período
            </h2>

            <div className="flex flex-col gap-4">
              {canMonitorSupport && (
                <Card className="pt-4">
                  <CardContent>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Data Inicial
                        </label>
                        <input
                          type="date"
                          value={dateFilter.start}
                          onChange={(e) =>
                            setDateFilter({
                              ...dateFilter,
                              start: e.target.value,
                            })
                          }
                          className="px-3 py-2 border rounded-md"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Data Final
                        </label>
                        <input
                          type="date"
                          value={dateFilter.end}
                          onChange={(e) =>
                            setDateFilter({
                              ...dateFilter,
                              end: e.target.value,
                            })
                          }
                          className="px-3 py-2 border rounded-md"
                        />
                      </div>
                      <Button
                        onClick={handleDateFilterChange}
                        className="sm:mt-6"
                      >
                        Atualizar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">
                          Total de Atendimentos
                        </p>
                        <p className="text-3xl font-bold text-blue-600">
                          {analytics.stats.total_tickets}
                        </p>
                      </div>
                      <TrendingUp className="w-10 h-10 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">
                          Resolvidos / Fechados
                        </p>
                        <p className="text-3xl font-bold text-green-600">
                          {analytics.stats.resolved_tickets}
                        </p>
                      </div>
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">
                          Taxa de Resolução
                        </p>
                        <p className="text-3xl font-bold text-purple-600">
                          {analytics.stats.resolution_rate}%
                        </p>
                      </div>
                      <Target className="w-10 h-10 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Tempo Médio</p>
                        <p className="text-3xl font-bold text-orange-600">
                          {formatDuration(analytics.stats.avg_duration_seconds)}
                        </p>
                      </div>
                      <Clock className="w-10 h-10 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">
                          Média de Satisfação
                        </p>
                        <div className="flex items-baseline gap-2">
                          <p className="text-3xl font-bold text-yellow-500">
                            {analytics.stats.avg_rating > 0
                              ? analytics.stats.avg_rating
                              : "-"}
                          </p>
                          {analytics.stats.avg_rating > 0 && (
                            <span className="text-xs text-gray-500 font-medium">
                              / 10
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">
                          Baseado em {analytics.stats.rating_count || 0}{" "}
                          avaliações
                        </p>
                      </div>
                      <Star className="w-10 h-10 text-yellow-400 fill-yellow-400 opacity-80" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Horários de Pico
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.peak_hours}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="hour"
                      label={{
                        value: "Hora do Dia",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />
                    <YAxis
                      label={{
                        value: "Volume",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" name="Atendimentos" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Atendentes em Destaque
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.top_attendants} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip />
                    <Bar dataKey="resolved" fill="#10b981" name="Resolvidos" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Evolução de Atendimentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.volume}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      label={{
                        value: "Data",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />
                    <YAxis
                      label={{
                        value: "Tickets",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      name="Atendimentos"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5" />
                  Atendimentos por Setor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.categories || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {(analytics.categories || []).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} tickets`, "Volume"]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Nível de Prioridade
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.priorities || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {(analytics.priorities || []).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            PRIORITY_COLORS[entry.name] ||
                            COLORS[index % COLORS.length]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} tickets`, "Volume"]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Top 10 Clientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {analytics.top_clients.map((client, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg overflow-hidden"
                  >
                    <div className="w-8 h-8 shrink-0 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-bold text-sm">
                        {index + 1}
                      </span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 truncate" title={client.name}>
                        {client.name}
                      </p>
                      <p className="text-xs text-gray-600 truncate">
                        {client.phone}
                      </p>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="font-bold text-blue-600">
                        {client.tickets}
                      </p>
                      <p className="text-xs text-gray-600">tickets</p>
                    </div>
                  </div>
                ))}
                {analytics.top_clients.length === 0 && (
                  <p className="text-center text-gray-500 py-4">
                    Nenhum dado disponível
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;
